import type { ListItem } from '@domain/list'
import { normalizedText } from '@domain/product'
import type { Product, ProductFamily } from '@domain/product'

/**
 * Uma sugestão do campo "O que falta?": um **grupo** ou uma **embalagem**.
 *
 * `tamanhos` acompanha o grupo porque é o que diz, na própria linha, que existe
 * uma comparação por trás dele — "Coca-Cola · 4 tamanhos" é ao mesmo tempo o
 * rótulo e o convite.
 *
 * `naLista` existe porque tocar numa sugestão **adiciona na hora**: sem o aviso,
 * o segundo toque criaria uma linha repetida em silêncio.
 */
export type Sugestao =
  | { tipo: 'grupo'; familia: ProductFamily; tamanhos: number; naLista: boolean }
  | { tipo: 'produto'; produto: Product; naLista: boolean }

/** Por seção, e não no total: as duas têm cabeçalho próprio na tela. */
const TETO_GRUPOS = 3
const TETO_PRODUTOS = 4

/**
 * Até três sugestões para o que está sendo digitado, **grupo antes de
 * embalagem**.
 *
 * ## Por que o grupo vem primeiro
 *
 * Numa lista de compras a granularidade certa é quase sempre o grupo: ninguém
 * decide entre a de 2 L e a lata ao escrever "coca" — decide na prateleira, e
 * é exatamente isso que a comparação de `/grupos/$id` responde. Vincular à
 * embalagem na hora de escrever fixa cedo demais uma escolha que depende de
 * preço que ainda vai ser visto.
 *
 * ## A embalagem do grupo sugerido VOLTOU a aparecer (decisão 218)
 *
 * A decisão 214 escondia a embalagem cujo grupo já estava sugerido, com o
 * argumento de que "Coca-Cola" e "Coca-Cola 2 L" lado a lado eram a mesma
 * coisa em duas granularidades. O argumento valia **naquele desenho**, em que
 * as duas eram pílulas iguais numa fileira só e nada dizia qual era qual.
 *
 * Com as seções rotuladas, a premissa caiu: "Grupos" e "Embalagens" são
 * cabeçalhos distintos, e escolher entre eles vira uma escolha informada em
 * vez de uma ambiguidade. Esconder passou a custar o caso real — querer **a
 * lata**, especificamente, e não achá-la porque o grupo existe.
 *
 * ## O casamento é o mesmo de sempre
 *
 * `normalizedText`, a mesma do matching da Fase 8: digitar "ype" acha "Ypê", e
 * "500ml" acha "500 ml". Um `includes` sobre o nome cru falharia nos dois, que
 * são exatamente as formas em que alguém digita com pressa.
 */
export function sugerir(
  produtos: Product[],
  familias: ProductFamily[],
  itens: Pick<ListItem, 'productId' | 'familyId' | 'completed'>[],
  texto: string,
): Sugestao[] {
  const chave = normalizedText(texto)
  if (chave.length < 3) return []

  // Só o que está PARA COMPRAR conta como "já na lista". Um item concluído é
  // compra feita, e querer de novo é o caso normal da semana seguinte.
  const pendentes = itens.filter((i) => !i.completed)
  const gruposNaLista = new Set(pendentes.map((i) => i.familyId).filter((id) => id !== null))
  const produtosNaLista = new Set(pendentes.map((i) => i.productId).filter((id) => id !== null))

  const vivos = produtos.filter((p) => p.archivedAt === null)

  const deGrupo: Sugestao[] = familias
    .filter((f) => f.archivedAt === null && f.normalizedName.includes(chave))
    .slice(0, TETO_GRUPOS)
    .map((familia) => ({
      tipo: 'grupo',
      familia,
      tamanhos: vivos.filter((p) => p.familyId === familia.id).length,
      naLista: gruposNaLista.has(familia.id),
    }))

  const deProduto: Sugestao[] = vivos
    .filter((p) => p.normalizedName.includes(chave))
    .slice(0, TETO_PRODUTOS)
    .map((produto) => ({ tipo: 'produto', produto, naLista: produtosNaLista.has(produto.id) }))

  return [...deGrupo, ...deProduto]
}

/** O nome que vai para o campo quando a pessoa toca no chip. */
export function nomeDaSugestao(s: Sugestao): string {
  return s.tipo === 'grupo' ? s.familia.name : s.produto.name
}
